import { Col, Collapse, Row, Skeleton, Tooltip } from 'antd';
import ChargeBasedOnActualWeightIconModal from 'components/ChargeBasedOnActualWeightIconModal';
import { ErrorBoundary } from 'components/ErrorBoundary';
import useProductDetailContent from 'components/Image/useProductDetailContent';
import ProductNote from 'components/ProductNote';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import { Viewport } from 'components/ViewportProvider';
import { IAddedProduct, IAddingProduct } from 'containers/ProductDetailsPage';
import { ContainerState, ProductReviewsType, RelatedProduct } from 'containers/ProductDetailsPage/types';
import QuantityInput from 'containers/QuantityInput';
import useContentResizeObserver from 'hooks/useContentResizeObserver';
import useInViewTracking from 'hooks/useInViewTracking';
import useProductUomInfo from 'hooks/useProductUomInfo';
import useResize from 'hooks/useResize';
import React, {
  ForwardRefRenderFunction,
  Fragment,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import translations from 'translations';
import { Language, Product, ProductVoucher, TrackingAddToCartAddFromType, Uom } from 'types/schema';
import { allowDecimalQuantity } from 'utils/allowDecimalQuantity';
import calculateVAT from 'utils/calculateVAT';
import { formatCurrencyCompact } from 'utils/formatCurrencyCompact';
import formatMoney, { formatCurrency } from 'utils/formatMoney';
import media from 'utils/mediaStyle';
import utilMessages from 'utils/messages';
import { EventName, sendGA4Event } from 'utils/triggerGA4Event';
import { getTimeDeliveryEarliest } from '../../utils/getTimeDeliveryEarliest';
import ProductNoteIcon from '../ProductNote/productNoteIcon.svg';
import AddToCartBottomBar from './_components/AddToCartBottomBar';
import KamereoProductList from './_components/KamereoProducts';
import ProductDetailsBanner from './_components/ProductDetailsBanner';
import ProductDetailsOriginInfo from './_components/ProductDetailsOriginInfo';
import ProductDetailsPolicyModal from './_components/ProductDetailsPolicyModal';
import RatingAndComments from './_components/RatingAndComments';
import RelatedProductDetails from './_components/RelatedProductDetails';
import SuggestBuyByBoxList from './_components/SuggestBuyByBox';
import ChevronRightIcon from './_icons/chevronRightIcon.svg';
import chevronTopIcon from './_icons/chevronTop.svg';
import SecurityIcon from './_icons/securityIcon.svg';
import TooltipIcon from './_icons/tooltipIcon.svg';
import TruckIcon from './_icons/truckIcon.svg';
import ProductGallery from './ProductGallery';
import ProductPrice from './ProductPrice';

const Container = styled.section`
  min-height: 400px;
`;
const Name = styled.h1`
  padding-left: 8pt;
  margin-bottom: 4px;
  font-size: 20px;
  font-weight: 500;
  line-height: 26px;
  padding-left: 0;
  color: #424242;

  ${({ isSticky }) =>
    isSticky &&
    `
    font-size: 16px;
    line-height: 20px;
  `}
`;

const ProductInfoContainer = styled.div`
  display: flex;
  align-items: center;
`;
const ProductUnitInfo = styled.p`
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: #757d73;

  ${({ isSticky }) =>
    isSticky &&
    `
      margin-bottom: 10px;
      `}
`;

const ProductUnitInfoDivider = styled.span`
  margin: 0 8px;
  width: 1px;
  height: 12px;
  background: #d5d7d5;
`;

const CalculateByKgText = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: #757d73;
`;

const TooltipIconWrapper = styled.div`
  margin-top: 2px;
  margin-left: 4px;
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const ProductDescriptionMobileTitle = styled.h2`
  margin-top: 24px;
  padding: 0;
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: -0.16px;
  color: #2f382c;
`;

const ProductDescriptionFirstContent = styled.pre`
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: -0.14px;
  color: #2f382c;
  margin: 8px 0;
`;

const ViewMoreBtn = styled.div`
  color: #136fd8;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  text-align: center;

  ${media.lg`
      text-align: left;
  `}
`;

const RightContentSectionContainer = styled(Col)`
  position: ${({ isDesktop, isSticky }) => (isDesktop && isSticky ? 'fixed' : 'relative')};
  top: ${({ isDesktop, isSticky }) => (isDesktop && isSticky ? '125px' : 'initial')};
  z-index: 10;
  animation: ${({ isSticky }) => (isSticky ? `fadeInAndSlideDown 0.3s ease-in-out forwards` : 'none')};

  @keyframes fadeInAndSlideDown {
    0% {
      opacity: 0;
      transform: translateY(-20px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const RightContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ProductDetailsPriceSection = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #eaebea;
`;

const VoucherDiscountWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 10px;
  gap: 8px;
`;

const VoucherDiscount = styled.div`
  position: relative;
  height: 20px;
  display: flex;
  align-items: center;
  padding: 0 8px;
  background: #ffecea;
`;

const VoucherDiscountText = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #e2483c;
`;

const ReceiveTimeContainer = styled.div`
  margin-top: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ReceiveTimeText = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: #596056;
`;

const ReceiveTimeValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: #2f382c;
`;

const Divider = styled.div`
  height: 1px;
  background: #eaebea;
  border-radius: 8px;
  margin: 16px 0;
`;

const PaymentPolicyWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;

const PaymentPolicyContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const PaymentPolicyText = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: #596056;
`;

// Product Description
const ProductDescriptionDetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${media.lg`
      margin-top: 40px;
  `}
`;
const ProductDescriptionDetailsTitle = styled.h1`
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  color: #343538;

  ${media.lg`
    font-size: 24px;
    font-weight: 500;
    line-height: 32px;
  `}
`;

const ProductDescriptionDetailsContainer = styled(Collapse)`
  .ant-collapse-header {
    padding: 10px 10px 10px 0px !important;

    ${media.lg`
          padding: 16px 16px 16px 0px !important;

      `}
  }
  .ant-collapse-item {
    background: #ffffff !important;
  }
  .ant-collapse-header-text {
    font-size: 14px !important;
    font-weight: 500 !important;
    letter-spacing: -0.32px !important;
    color: #2f382c !important;

    ${media.lg`
          font-size: 16px !important;
    `}
  }
  .ant-collapse-arrow {
    font-size: 16px !important;
    color: #2f382c !important;
  }
  .ant-collapse-content {
    background: #ffffff !important;
  }
  .ant-collapse-content-box {
    padding: 4px 0px 16px 0px !important;
  }
  .ant-collapse-extra {
    display: none !important;
  }
`;

const ProductDescriptionDetailsLabel = styled.h2`
  font-size: 14px !important;
  font-weight: 500 !important;
  letter-spacing: -0.32px !important;
  color: #2f382c !important;

  ${media.lg`
      font-size: 16px !important;
  `}
`;

const ProductDescriptionDetailsContent = styled.div`
  font-size: 15px;
  color: rgba(0, 0, 0, 0.87);
  font-weight: 400;
  line-height: 23px;
  p {
    img {
      max-width: 100%;
    }
  }
`;

const ExclusiveNoteText = styled.div`
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: -0.14px;
  font-style: normal;
  color: #596056;
`;

const OutOfStockButton = styled.div`
  margin-top: 16px;
  width: 100%;
  padding: 12px 16px;
  border-radius: 8px;
  background: #f5f5f4;
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  color: #bcbfbb;
  text-align: center;
`;

const ProductInfoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
  flex-wrap: wrap;
`;

const TagBookInAdvance = styled.div`
  flex-shrink: 0;
  padding: 4px 6px;
  background: #ffecdf;
  color: #e56910;
  font-size: 14px;
  font-weight: 500;
  border-radius: 4px;
  width: fit-content;
  display: flex;
  align-items: center;

  path {
    stroke: #e56910;
  }
`;

const TagBestSeller = styled.div`
  flex-shrink: 0;
  padding: 4px 6px;
  background: #ffecea;
  color: #e2483c;
  font-size: 14px;
  font-weight: 500;
  border-radius: 4px;
  width: fit-content;
`;

const SoldAmount = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: #596056;
  span:last-child {
    font-weight: 500;
    color: #2f382c;
  }
`;

export const componentPermissions = {
  addToCartPermission: 'ORDER_PLACE',
  addFavoritePermission: 'BUYER_FAVORITE_ITEM_ADD',
};

interface IProps {
  product: ContainerState['product'];
  added: number;
  adding: boolean;
  addedRelatedProducts: IAddedProduct;
  addingRelatedProducts: IAddingProduct;
  permissions: string[];
  onToggleFavorite: (data: { product: Product }) => void;
  onOpenNote: (product: Product) => void;
  onDeleteNote: (product: Product) => void;
  gotoSignIn: () => void;
  kamereoProducts?: Product[];
  productsInSameProductFamily?: Product[];
  relatedProducts?: RelatedProduct[];
  isHaveProductDetailsDescription?: boolean;
  vouchersForProduct: ProductVoucher[];
  productReviews?: ProductReviewsType;
}

const ProductDescriptionDetailItemContent = ({ name, content }: { content: string; name?: string }) => {
  const productDetailData = useProductDetailContent(content || '', name || '');

  return <ProductDescriptionDetailsContent dangerouslySetInnerHTML={{ __html: productDetailData?.description }} />;
};

const VoucherTagList = ({ vouchersForProduct }) => {
  return (
    <VoucherDiscountWrapper>
      {vouchersForProduct?.map((item, index) => (
        <VoucherDiscount key={index}>
          <div
            style={{
              width: '4px',
              height: '4px',
              position: 'absolute',
              top: '4px',
              left: '-2px',
              background: '#ffffff',
              borderRadius: '50%',
            }}
          />
          <div
            style={{
              width: '4px',
              height: '4px',
              position: 'absolute',
              top: '12px',
              left: '-2px',
              background: '#ffffff',
              borderRadius: '50%',
            }}
          />
          <VoucherDiscountText>
            <FormattedMessage {...utilMessages.discount} />
            &nbsp;
            {item.type === 'PERCENTAGE_DISCOUNT' ? `${item.value}%` : formatCurrency(Number(item.value))}
          </VoucherDiscountText>
          <div
            style={{
              width: '4px',
              height: '4px',
              position: 'absolute',
              top: '4px',
              right: '-2px',
              background: '#ffffff',
              borderRadius: '50%',
            }}
          />
          <div
            style={{
              width: '4px',
              height: '4px',
              position: 'absolute',
              top: '12px',
              right: '-2px',
              background: '#ffffff',
              borderRadius: '50%',
            }}
          />
        </VoucherDiscount>
      ))}
    </VoucherDiscountWrapper>
  );
};

const ProductInfoComponent: ForwardRefRenderFunction<HTMLElement, IProps> = (props, ref) => {
  const loggedIn = !!localStorage.getItem('token');
  const lang = localStorage.getItem('lang') || Language.Vi;
  const { isDesktop } = useResize();

  const {
    product,
    permissions,
    onToggleFavorite,
    onOpenNote,
    onDeleteNote,
    gotoSignIn,
    kamereoProducts,
    productsInSameProductFamily,
    relatedProducts,
    isHaveProductDetailsDescription,
    addedRelatedProducts,
    addingRelatedProducts,
    added,
    vouchersForProduct,
  } = props;

  const {
    note,
    preservationLocal,
    origin: productOrigin,
    description,
    detailsV2,
    vat,
    allowAddToCart,
    allowPurchase,
    private: isPrivate,
    brand,
    uom,
    loading,
    name,
    tags,
    totalSold,
  } = product;

  const descriptionDetailsRef = useRef<HTMLDivElement>(null);
  const mainContentRef = useRef<HTMLDivElement>(null);
  const rightSectionRef = useRef<HTMLDivElement>(null);
  const middleContentRef = useRef<HTMLDivElement>(null);
  const relatedProductsRef = useRef<HTMLElement>(null);

  const [isSticky, setIsSticky] = useState(false);
  const [openPaymentPolicy, setOpenPaymentPolicy] = useState(false);
  const [currentQuantity, setCurrentQuantity] = useState('');

  const { uomInfo, baseUom: currBaseUom, netUom: currNetUom, uom: currUom } = useProductUomInfo(product);

  const productDescriptionDetail = useMemo(() => {
    const items = [...(detailsV2 || [])];
    let contentNoTitle;
    if (!items?.length) {
      return {
        contentNoTitle: contentNoTitle,
        items: [],
      };
    }

    if (!items[0]?.title) {
      contentNoTitle = <ProductDescriptionDetailItemContent content={items.shift()?.content || ''} name={name || ''} />;
    }

    return {
      contentNoTitle: contentNoTitle,
      items: items?.map((item, index) => ({
        key: String(index + 1),
        label: <ProductDescriptionDetailsLabel>{item?.title}</ProductDescriptionDetailsLabel>,
        children: <ProductDescriptionDetailItemContent name={item?.title || ''} content={item?.content || ''} />,
        extra: item?.title,
      })),
    };
  }, [detailsV2]);

  const totalPrice = useMemo(() => {
    if (!currentQuantity) return 0;
    const quantity = parseFloat(currentQuantity);
    if (isNaN(quantity)) return 0;
    return Math.round(
      calculateVAT(product?.priceV2?.price || 0, product?.priceV2?.vat || 0, Boolean(product?.priceV2?.vatIncluded))
        .beforeVat * quantity,
    );
  }, [currentQuantity, product.priceV2]);

  const calculateSticky = useCallback(() => {
    if (!rightSectionRef.current || !mainContentRef.current || !relatedProductsRef.current || !isDesktop || loading) {
      return;
    }

    const rightSectionHeight = rightSectionRef.current.offsetHeight;
    const mainContentTop = mainContentRef.current.getBoundingClientRect().top + window.scrollY;
    const relatedProductsTop = relatedProductsRef.current.getBoundingClientRect().top + window.scrollY;

    const stickyPoint = mainContentTop + rightSectionHeight - 130; // height of header 130px
    const stickyEndPoint = relatedProductsTop - rightSectionHeight - 100;

    if (window.scrollY > stickyPoint && window.scrollY < stickyEndPoint) {
      setIsSticky(true);
    } else {
      setIsSticky(false);
    }
  }, [isDesktop, loading]);

  const trackReviewInView = useCallback(() => {
    sendGA4Event(EventName.PDP_VIEW_REVIEW_SECTION, {
      product_id: product?.id,
    });
  }, [product?.id]);

  useContentResizeObserver(mainContentRef, calculateSticky);
  const reviewSectionRef = useInViewTracking(trackReviewInView, true, 0.3);

  // effects
  useEffect(() => {
    const handleScroll = () => {
      calculateSticky();
    };

    handleScroll();

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [calculateSticky]);

  useEffect(() => {
    if (added) {
      setCurrentQuantity(added.toString());
    } else {
      setCurrentQuantity('');
    }
  }, [added]);

  return (
    <ErrorBoundary>
      <Viewport.Consumer>
        {({ width }) => {
          const isDesktop = width >= 1024;
          return (
            <Container ref={mainContentRef}>
              <Row gutter={[24, 16]}>
                <Col lg={16} xs={24}>
                  <Row gutter={[24, 16]}>
                    <Col lg={12} xs={24}>
                      <ProductGallery
                        product={product}
                        lang={lang as Language}
                        loggedIn={loggedIn}
                        componentPermissions={componentPermissions}
                        permissions={permissions}
                        onToggleFavorite={() => {
                          onToggleFavorite({ product: product });
                          sendGA4Event(EventName.PDP_ADD_TO_FAVORITE, {
                            product_id: product.id,
                            status: product.isFavorite ? 'remove' : 'add',
                          });
                        }}
                        gotoSignIn={gotoSignIn}
                      />
                    </Col>
                    <Col lg={12} xs={24} ref={middleContentRef}>
                      {loading ? (
                        <Skeleton loading={loading} paragraph={{ rows: 3, width: '100%' }} active />
                      ) : (
                        <Fragment>
                          <ProductInfoHeader>
                            {(tags || []).includes('BOOK_IN_ADVANCE') ? (
                              <Tooltip title={<FormattedMessage {...utilMessages.bookInAdvanceDescription} />}>
                                <TagBookInAdvance>
                                  <FormattedMessage {...utilMessages.bookInAdvanceTag} />
                                  <TooltipIconWrapper>
                                    <SVGIcon src={TooltipIcon} renderAsImg={false} width={15} height={15} />
                                  </TooltipIconWrapper>
                                </TagBookInAdvance>
                              </Tooltip>
                            ) : (tags || []).includes('BEST_SELLER') && (totalSold || 0) >= 50 ? (
                              <TagBestSeller>
                                <FormattedMessage {...utilMessages.bestSeller} />
                              </TagBestSeller>
                            ) : null}
                            <SoldAmount>
                              <FormattedMessage {...utilMessages.soldAmount} /> {` `}
                              <span>{formatCurrencyCompact(Number(totalSold || 0), lang)}</span>
                            </SoldAmount>
                          </ProductInfoHeader>
                          <Name>{name}</Name>
                          <ProductInfoContainer>
                            <ProductUnitInfo>{uomInfo}</ProductUnitInfo>
                            {!!uom && ['KILOGRAM'].includes(uom) && (
                              <>
                                <ProductUnitInfoDivider />
                                <CalculateByKgText>
                                  <FormattedMessage {...utilMessages.paymentByUomKg} />
                                </CalculateByKgText>
                                <TooltipIconWrapper>
                                  <ChargeBasedOnActualWeightIconModal
                                    triggerGA4Event={() => {
                                      sendGA4Event(EventName.PDP_CLICK_ACTUAL_WEIGHT, {
                                        product_id: product.id,
                                        popup_displayed: true,
                                      });
                                    }}
                                  />
                                </TooltipIconWrapper>
                              </>
                            )}
                            {!isDesktop && !note && (
                              <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                <SVGIcon
                                  src={ProductNoteIcon}
                                  onClick={() => {
                                    onOpenNote(product);
                                  }}
                                />
                              </div>
                            )}
                          </ProductInfoContainer>
                          {!isDesktop && !!note && (
                            <>
                              <div
                                style={{
                                  marginTop: 8,
                                }}
                              />
                              <ProductNote
                                isOnlyShowNoteIcon={true}
                                note={note}
                                isHorizontal={true}
                                onClickOpenNote={() => {
                                  onOpenNote(product);
                                }}
                              />
                            </>
                          )}

                          {isDesktop ? (
                            <>
                              {!!productsInSameProductFamily?.length && !!product && (
                                <div>
                                  <SuggestBuyByBoxList
                                    suggestBuyProducts={productsInSameProductFamily || []}
                                    currentProduct={product}
                                  />
                                </div>
                              )}
                              <ProductDetailsOriginInfo
                                productOrigin={productOrigin || ''}
                                vat={vat || 0}
                                brand={brand as any}
                                preservationLocal={preservationLocal || ''}
                                isKamereoProduct={tags?.some((tag) => tag?.includes('KAMEREO'))}
                              />
                              {!!description?.trim() && (
                                <>
                                  <ProductDescriptionFirstContent>{description}</ProductDescriptionFirstContent>
                                  {isHaveProductDetailsDescription && (
                                    <ViewMoreBtn
                                      onClick={() => {
                                        if (descriptionDetailsRef.current) {
                                          window.scrollTo({
                                            top: descriptionDetailsRef.current.offsetTop,
                                            behavior: 'smooth',
                                          });
                                        }
                                        sendGA4Event(EventName.PDP_CLICK_VIEW_MORE, {
                                          product_id: product.id,
                                          section: 'description',
                                        });
                                      }}
                                    >
                                      {translations(utilMessages.readMore)}
                                    </ViewMoreBtn>
                                  )}
                                </>
                              )}
                            </>
                          ) : (
                            <>
                              <div style={{ marginTop: 8 }} />
                              {(!isPrivate || allowPurchase) && (
                                <ProductPrice
                                  product={product}
                                  uom={currUom}
                                  baseUom={currBaseUom}
                                  netUom={currNetUom}
                                  lang={lang as Language}
                                />
                              )}
                              {!!vouchersForProduct?.length && (
                                <VoucherTagList vouchersForProduct={vouchersForProduct} />
                              )}
                              {!!productsInSameProductFamily?.length && !!product && (
                                <SuggestBuyByBoxList
                                  suggestBuyProducts={productsInSameProductFamily || []}
                                  currentProduct={product}
                                />
                              )}
                              <div style={{ marginTop: 16 }} />

                              {!!kamereoProducts?.length && (
                                <KamereoProductList kamereoProducts={kamereoProducts || []} productId={product?.id} />
                              )}
                              <ReceiveTimeContainer>
                                <SVGIcon src={TruckIcon} width={20} height={20} />
                                <ReceiveTimeText>
                                  <FormattedMessage {...utilMessages.receiveTimeEarliest} />{' '}
                                  <ReceiveTimeValue>{getTimeDeliveryEarliest()}</ReceiveTimeValue>
                                </ReceiveTimeText>
                              </ReceiveTimeContainer>
                              <Divider />
                              <PaymentPolicyWrapper
                                onClick={() => {
                                  setOpenPaymentPolicy(true);
                                  sendGA4Event(EventName.PDP_CLICK_SHIPPING_POLICY, {
                                    product_id: product.id,
                                    popup_displayed: true,
                                  });
                                }}
                              >
                                <PaymentPolicyContainer>
                                  <SVGIcon src={SecurityIcon} width={20} height={20} />
                                  <PaymentPolicyText>
                                    <FormattedMessage {...utilMessages.securePayment} />
                                  </PaymentPolicyText>
                                </PaymentPolicyContainer>
                                <SVGIcon src={ChevronRightIcon} width={12} height={12} />
                              </PaymentPolicyWrapper>
                              <ProductDescriptionMobileTitle>
                                <FormattedMessage {...utilMessages.productDescription} />
                              </ProductDescriptionMobileTitle>
                              <ProductDetailsOriginInfo
                                productOrigin={productOrigin || ''}
                                vat={vat || 0}
                                brand={brand as any}
                                preservationLocal={preservationLocal || ''}
                                isKamereoProduct={tags?.some((tag) => tag?.includes('KAMEREO'))}
                              />
                              {!!description?.trim() && (
                                <>
                                  {productDescriptionDetail?.contentNoTitle}
                                  <ProductDescriptionDetailsContainer
                                    expandIconPosition={'end'}
                                    items={productDescriptionDetail?.items}
                                    bordered={false}
                                    onChange={(keys) => {
                                      sendGA4Event(EventName.PDP_CLICK_DESCRIPTION, {
                                        product_id: product.id,
                                        section_name:
                                          productDescriptionDetail?.items?.find((item) => keys.includes(item.key))
                                            ?.extra || '',
                                        action: keys.length ? 'expand' : 'collapse',
                                      });
                                    }}
                                    expandIcon={({ isActive }) => (
                                      <SVGIcon
                                        src={chevronTopIcon}
                                        width={15}
                                        height={8}
                                        renderAsImage={false}
                                        rotate={isActive ? 0 : 90}
                                      />
                                    )}
                                  />
                                </>
                              )}
                              <div ref={reviewSectionRef}>
                                <RatingAndComments />
                              </div>
                              <div style={{ margin: '32px 0px' }}>
                                <ProductDetailsBanner tags={tags} product_id={product.id} />
                              </div>
                            </>
                          )}
                        </Fragment>
                      )}
                    </Col>
                  </Row>
                  {isDesktop && (
                    <>
                      {!!detailsV2?.length && (
                        <ProductDescriptionDetailsWrapper ref={descriptionDetailsRef}>
                          <ProductDescriptionDetailsTitle>
                            <FormattedMessage {...utilMessages.productDescription} />
                          </ProductDescriptionDetailsTitle>
                          {productDescriptionDetail?.contentNoTitle}

                          <ProductDescriptionDetailsContainer
                            expandIconPosition={'end'}
                            items={productDescriptionDetail?.items}
                            bordered={false}
                            onChange={(keys) => {
                              sendGA4Event(EventName.PDP_CLICK_DESCRIPTION, {
                                product_id: product.id,
                                section_name:
                                  productDescriptionDetail?.items?.find((item) => keys.includes(item.key))?.extra || '',
                                action: keys.length ? 'expand' : 'collapse',
                              });
                            }}
                            expandIcon={({ isActive }) => (
                              <SVGIcon
                                src={chevronTopIcon}
                                width={15}
                                height={8}
                                renderAsImage={false}
                                rotate={isActive ? 0 : 90}
                              />
                            )}
                          />
                        </ProductDescriptionDetailsWrapper>
                      )}
                      <div ref={reviewSectionRef}>
                        <RatingAndComments />
                      </div>
                    </>
                  )}
                </Col>

                {isDesktop && (
                  <RightContentSectionContainer
                    lg={8}
                    xs={24}
                    isDesktop={isDesktop}
                    isSticky={isSticky}
                    style={{
                      right:
                        isDesktop && isSticky
                          ? `${
                              window.innerWidth -
                              (mainContentRef?.current?.getBoundingClientRect().right || window.innerWidth) -
                              13
                            }px`
                          : 'initial',
                      width: isDesktop && isSticky ? middleContentRef?.current?.offsetWidth || 0 : '100%',
                    }}
                  >
                    {loading ? (
                      <Skeleton loading={loading} paragraph={{ rows: 3, width: '100%' }} active />
                    ) : (
                      <RightContentSection ref={rightSectionRef}>
                        <ProductDetailsPriceSection>
                          {isSticky && (
                            <>
                              <Name isSticky={isSticky}>{name}</Name>
                              <ProductUnitInfo isSticky={isSticky}>{uomInfo}</ProductUnitInfo>
                            </>
                          )}
                          {(!isPrivate || allowPurchase) && (
                            <ProductPrice
                              product={product}
                              uom={currUom}
                              baseUom={currBaseUom}
                              netUom={currNetUom}
                              lang={lang as Language}
                            />
                          )}
                          {!!vouchersForProduct?.length && <VoucherTagList vouchersForProduct={vouchersForProduct} />}
                          {(!loggedIn || permissions.includes(componentPermissions.addToCartPermission)) &&
                            (allowAddToCart ? (
                              <>
                                <div
                                  style={{
                                    margin: '16px 0',
                                  }}
                                >
                                  <ProductNote
                                    note={note}
                                    isHorizontal={true}
                                    onClickOpenNote={() => {
                                      onOpenNote(product);
                                    }}
                                  />
                                </div>

                                <QuantityInput
                                  product={product}
                                  allowDecimal={allowDecimalQuantity(uom)}
                                  addFrom={TrackingAddToCartAddFromType.ProductDetail}
                                  isProductDetails={true}
                                  ga4Callback={(value) => {
                                    sendGA4Event(EventName.PDP_ADD_TO_CART, {
                                      product_id: product.id,
                                      product_name: product.name,
                                      quantity: value,
                                      source: 'pdp',
                                    });
                                  }}
                                />

                                <ReceiveTimeContainer>
                                  <SVGIcon src={TruckIcon} width={20} height={20} />
                                  <ReceiveTimeText>
                                    <FormattedMessage {...utilMessages.receiveTimeEarliest} />{' '}
                                    <ReceiveTimeValue>{getTimeDeliveryEarliest()}</ReceiveTimeValue>
                                  </ReceiveTimeText>
                                </ReceiveTimeContainer>
                              </>
                            ) : !allowPurchase ? (
                              <>
                                <ExclusiveNoteText>
                                  <FormattedMessage {...utilMessages.onlyForSpecialBusiness} />
                                </ExclusiveNoteText>
                                <OutOfStockButton>
                                  <FormattedMessage {...utilMessages.exclusive} />
                                </OutOfStockButton>
                              </>
                            ) : (
                              <OutOfStockButton>
                                <FormattedMessage {...utilMessages.outStock} />
                              </OutOfStockButton>
                            ))}
                          <Divider />
                          <PaymentPolicyWrapper onClick={() => setOpenPaymentPolicy(true)}>
                            <PaymentPolicyContainer>
                              <SVGIcon src={SecurityIcon} width={20} height={20} />
                              <PaymentPolicyText>
                                <FormattedMessage {...utilMessages.securePayment} />
                              </PaymentPolicyText>
                            </PaymentPolicyContainer>
                            <SVGIcon src={ChevronRightIcon} width={12} height={12} />
                          </PaymentPolicyWrapper>
                        </ProductDetailsPriceSection>
                        {!isSticky && !!kamereoProducts?.length && (
                          <KamereoProductList kamereoProducts={kamereoProducts || []} productId={product?.id} />
                        )}
                        {!isSticky && <ProductDetailsBanner tags={tags} />}
                      </RightContentSection>
                    )}
                  </RightContentSectionContainer>
                )}
              </Row>
              <aside ref={relatedProductsRef} aria-label={translations(utilMessages.similarProducts)}>
                <RelatedProductDetails
                  productId={product.id}
                  loggedIn={loggedIn}
                  gotoSignIn={gotoSignIn}
                  relatedProducts={relatedProducts}
                  addedRelatedProducts={addedRelatedProducts}
                  addingRelatedProducts={addingRelatedProducts}
                  permissions={permissions}
                  onToggleFavorite={onToggleFavorite}
                  onOpenNote={onOpenNote}
                  onDeleteNote={onDeleteNote}
                />
              </aside>

              <AddToCartBottomBar
                loggedIn={loggedIn}
                permissions={permissions}
                loading={loading}
                allowAddToCart={!!allowAddToCart}
                allowPurchase={!!allowPurchase}
                totalPrice={totalPrice}
                product={product}
                uom={uom as Uom}
                setCurrentQuantity={setCurrentQuantity}
              />
              <ProductDetailsPolicyModal
                openPaymentPolicy={openPaymentPolicy}
                setOpenPaymentPolicy={setOpenPaymentPolicy}
              />
            </Container>
          );
        }}
      </Viewport.Consumer>
    </ErrorBoundary>
  );
};

export default forwardRef<HTMLElement, IProps>(ProductInfoComponent);
